import * as angular from 'angular';

import { TagDateValue, IStatistics } from '../../models';
import { AggregationMode } from '../../models/enum';
import { BusinessKpi } from '../../models/DTO';
import { IStatisticsService } from './';
import { IDateRangeService } from '../general';
import { IwidgetIntervalHelperService } from '../helpers';
import { IRequestService } from '../../services';

export interface ICalculatedValuesService {
    count(): angular.IPromise<number>;
    getAllBetweenDates(beginDate, endDate): angular.IPromise<any>;
    getBetweenDatesForIdentifier(identifierGuid: string, beginDate, endDate): angular.IPromise<any>;
    getIdentifierStatistics(guid: string, force?: boolean): angular.IPromise<IStatistics>;
    getCompressedValueBetweenDatesForIdentifier(identifierGuid: string, value: string, beginDate?: string, endDate?: string): angular.IPromise<TagDateValue[]>;
    getValueBetweenDatesForIdentifier(identifierGuid: string, value: string, beginDate?: string, endDate?: string): angular.IPromise<TagDateValue[]>;
    getAggregatedValueBetweenDates(identifierGuid: string, value: string): angular.IPromise<BusinessKpi>;
    getAggregatedValueBetweenDates(identifierGuid: string, value: string, beginDate: string, endDate: string, aggregationMode: AggregationMode): angular.IPromise<BusinessKpi>;
    getValueAndStatus(guid: string, endDate): angular.IPromise<any>;
}

export class CalculatesValuesService implements ICalculatedValuesService {
    static serviceId = 'calculatedValuesService';

    static $inject = ['$q', 'requestService', 'dateRangeService', 'statisticsService',
        'widgetIntervalHelperService'];

    constructor(
        private _$q: angular.IQService,
        private _requestService: IRequestService,
        private _dateRangeService: IDateRangeService,
        private _statisticsService: IStatisticsService,
        private _widgetIntervalHelperService: IwidgetIntervalHelperService) {
    }

    private controllerUrl() {
        return this._requestService.getUrlV1() + 'calculatedValues/';
    }

    count(): angular.IPromise<number> {
        const url = this.controllerUrl() + 'count';
        return this._requestService.get(url);
    }

    getBetweenDatesForIdentifier(identifierGuid: string, beginDate, endDate, force: boolean = false): angular.IPromise<any> {
        const url = this.controllerUrl()
            + '?identifierGuid=' + identifierGuid
            + '&beginDateTime=' + beginDate
            + '&endDateTime=' + endDate;
        if (force) {
            this._requestService.cleanCache(url);
        }
        return this._requestService.get(url);
    }

    getAllBetweenDates(beginDate: Date, endDate: Date): angular.IPromise<any> {
        const url = this.controllerUrl()
            + '?beginDateTime=' + beginDate
            + '&endDateTime=' + endDate;
        return this._requestService.get(url);
    }

    getIdentifierStatistics(guid: string, force: boolean = false): angular.IPromise<IStatistics> {
        const defered = this._$q.defer<IStatistics>();
        if (this._dateRangeService.initialized()) {
            const self = this;
            const interval = this._widgetIntervalHelperService.getCurrentInclusiveInterval();
            this.getBetweenDatesForIdentifier(guid, interval.beginDate, interval.endDate, force)
                .then(data => {
                    const result = self._statisticsService.getStatistics(data);
                    defered.resolve(result);
                });
        } else {
            defered.resolve();
        }
        return defered.promise;
    }

    getCompressedValueBetweenDatesForIdentifier(identifierGuid: string, value: string, beginDate?: string, endDate?: string): angular.IPromise<TagDateValue[]> {
        if (this._dateRangeService.initialized()) {
            if (!beginDate) {
                beginDate = this._dateRangeService.beginDate() as string;
                endDate = this._dateRangeService.endDate() as string;
            }
            const url = this.controllerUrl() + 'GetCompressedValue'
                + '?identifierGuid=' + identifierGuid
                + '&beginDateTime=' + beginDate
                + '&endDateTime=' + endDate
                + '&value=' + value;
            return this._requestService.get(url);
        } else {
            const defered = this._$q.defer<TagDateValue[]>();
            defered.reject();
            return defered.promise;
        }
    }

    getValueBetweenDatesForIdentifier(identifierGuid: string,
        value: string,
        beginDate?: string,
        endDate?: string): angular.IPromise<TagDateValue[]> {
        if (this._dateRangeService.initialized()) {
            beginDate = beginDate ? beginDate : this._dateRangeService.beginDate() as string;
            endDate = endDate ? endDate : this._dateRangeService.endDate() as string;
            const params = {
                identifierGuid: identifierGuid,
                beginDateTime: beginDate,
                endDateTime: endDate,
                value: value
            };
            const url = this.controllerUrl() + 'GetValue';
            return this._requestService.get(url, params);
        } else {
            const defered = this._$q.defer<TagDateValue[]>();
            defered.reject();
            return defered.promise;
        }
    }

    getAggregatedValueBetweenDates(identifierGuid: string, value: string): angular.IPromise<BusinessKpi>;
    getAggregatedValueBetweenDates(identifierGuid: string, value: string, beginDate: string, endDate: string, aggregationMode: AggregationMode): angular.IPromise<BusinessKpi>;
    getAggregatedValueBetweenDates(identifierGuid: string, value: string, beginDate?: string, endDate?: string, aggregationMode?: AggregationMode): angular.IPromise<BusinessKpi> {
        if (this._dateRangeService.initialized()) {
            const url = this.controllerUrl() + 'getAggregatedValueForIdentifier';
            const params = {
                identifierGuid: identifierGuid,
                beginDateTime: beginDate ? beginDate : this._dateRangeService.beginDate() as string,
                endDateTime: endDate ? endDate : this._dateRangeService.endDate() as string,
                value: value,
                aggregationMode: aggregationMode ? aggregationMode : 1
            };
            return this._requestService.get(url, params);
        } else {
            return this._$q.reject('Date range not initialized.');
        }
    }

    getValueAndStatus(guid: string, endDate): angular.IPromise<any> {
        const url = this.controllerUrl() + 'getValueAndStatus';
        const params = {
            identifierGuid: guid,
            dateTime: endDate
        };
        return this._requestService.get(url, params);
    }
}